import InfoOutlined from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  Box,
  Button,
  Collapse,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { amber, blue } from "@mui/material/colors";
import {
  BarPlot,
  ChartContainerPro,
  ChartsAxisHighlight,
  ChartsTooltipContainer,
  ChartsXAxis,
  ChartsYAxis,
  useAxesTooltip,
} from "@mui/x-charts-pro";
import { BigTooltip } from "components/BigTooltip";
import ChartTooltip from "components/Dashboard/ChartTooltip";
import { federalWasteGeneratorStatusToLabel } from "encamp-shared/src/waste";
import {
  RcraInfoFederalWasteGeneratorStatus,
  WasteGenerationChartsQuery,
} from "generated-graphql/graphql";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatNumber } from "util/formatNumber";

import {
  GeneratorStatusKind,
  getFederalGeneratorStatusColorSettings,
} from "../getFederalGeneratorStatusColorSettings";
import {
  AxisThreshold,
  ThresholdReferenceLines,
  ThresholdYAxisIndicators,
} from "../thresholdAxisIndicators";
import { WasteValueDisplay } from "../WasteShipments/HazardousWasteShipmentsGraph/WasteValueDisplay";
import { colorMap, formatMonth } from "../WasteShipments/utils";
import { ACUTE_COLOR, getGenerationThresholds } from "./generationThresholds";
import { MonthDetailsView } from "./MonthDetailsView";

// Federal regime: orange for the bar (lighter) and threshold line (darker, in
// generationThresholds). Pulled from the shared `colorMap` so the federal
// hazardous bar reads as the same color as "Vendor Hazardous" on the Waste
// Shipments chart.
const FEDERAL_BAR_COLOR = colorMap["Vendor Hazardous"];
// State regime: blue for both bar and threshold line — see generationThresholds
// for the matching darker line shade. Keeping each regime in one color family
// avoids the "is this bar related to that line?" puzzle.
const STATE_ONLY_BAR_COLOR = blue[300];

type GenerationRow =
  WasteGenerationChartsQuery["facilityWasteGenerationCharts"]["generationByMonth"][number];

type Props = {
  data: GenerationRow[];
  totalFederalHazardousLbs: number;
  totalStateOnlyLbs: number;
  totalAcuteHazardousLbs: number;
  highestCalculatedGeneratorStatus: RcraInfoFederalWasteGeneratorStatus | null;
  facilityState?: string | null;
};

/**
 * Axis-triggered tooltip mirroring the Waste Shipments tile: bold month title
 * on the left with the calculated federal generator status pill on the right,
 * then a color-swatch legend for each series (zero values filtered).
 */
function GenerationAxisTooltip({
  year,
  monthlyStatus,
}: {
  year: string | null;
  monthlyStatus: Array<RcraInfoFederalWasteGeneratorStatus | null>;
}) {
  const tooltipData = useAxesTooltip();
  if (!tooltipData?.length) return null;

  const axisEntry = tooltipData[0];
  const rawMonth = axisEntry.axisFormattedValue ?? "";
  const month = rawMonth.includes("\n") ? rawMonth.split("\n")[0] : rawMonth;
  const monthYear = year ? `${month} ${year}` : month;
  const status = monthlyStatus[axisEntry.dataIndex ?? -1] ?? null;
  const statusColors = getFederalGeneratorStatusColorSettings(
    status,
    GeneratorStatusKind.CALCULATED
  );

  const labels = (axisEntry.seriesItems ?? []).flatMap((item) => {
    const value = typeof item.value === "number" ? item.value : null;
    if (value == null || value <= 0) return [];
    return [
      {
        color: item.color ?? "",
        value: (
          <Stack direction="row" alignItems="center" key={String(item.seriesId)}>
            <Typography variant="body2">{item.formattedLabel ?? ""}</Typography>
            <Typography variant="body2">&nbsp;({formatNumber(value)} lbs)</Typography>
          </Stack>
        ),
      },
    ];
  });

  return (
    <ChartTooltip
      title={
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ width: 300, mb: 1 }}
        >
          <Typography variant="h6" fontWeight={800}>
            {monthYear}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="textSecondary">
              Calculated
            </Typography>
            {status ? (
              <Box
                sx={{
                  px: 1,
                  py: 0.25,
                  borderRadius: 999,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  backgroundColor: statusColors.backgroundColor,
                  border: `1px solid ${statusColors.borderColor}`,
                  color: statusColors.textColor,
                }}
              >
                {federalWasteGeneratorStatusToLabel(status)}
              </Box>
            ) : (
              <Typography variant="body2" color="textSecondary">
                None
              </Typography>
            )}
          </Stack>
        </Stack>
      }
      labels={labels}
    />
  );
}

type BandCell = {
  month: string;
  reportedStatus: RcraInfoFederalWasteGeneratorStatus | null;
  calculatedStatus: RcraInfoFederalWasteGeneratorStatus | null;
  /** True when shipment-derived status disagrees with generation-derived status. */
  divergesFromShipment: boolean;
  /** Shipment-derived calculated status — surfaced in the divergence tooltip. */
  shipmentCalculatedStatus: RcraInfoFederalWasteGeneratorStatus | null;
};

type GroupedBandCell = BandCell & {
  reportedStart: boolean;
  reportedEnd: boolean;
  calculatedStart: boolean;
  calculatedEnd: boolean;
};

/**
 * Two-row status band beneath the chart mirroring Waste Shipments
 * `ClassificationBars`: `Reported` (RCRAInfo-notified status, same source the
 * shipments tile uses) above `Calculated` (derived from this month's
 * generation lbs). Consecutive months sharing a status collapse into a single
 * label-bearing cell; both rows hover-couple with the chart column.
 */
function StatusBand({
  monthly,
  hoveredMonth,
  onMonthHover,
  onMonthLeave,
}: {
  monthly: BandCell[];
  hoveredMonth: string | null;
  onMonthHover: (month: string) => void;
  onMonthLeave: () => void;
}) {
  const groups: GroupedBandCell[] = useMemo(() => {
    return monthly.map((cell, index) => {
      const prev = monthly[index - 1];
      const next = monthly[index + 1];
      return {
        ...cell,
        reportedStart: !prev || prev.reportedStatus !== cell.reportedStatus,
        reportedEnd: !next || next.reportedStatus !== cell.reportedStatus,
        calculatedStart: !prev || prev.calculatedStatus !== cell.calculatedStatus,
        calculatedEnd: !next || next.calculatedStatus !== cell.calculatedStatus,
      };
    });
  }, [monthly]);

  return (
    <Box mb={2} mt={1}>
      <StatusRow
        label="Reported"
        kind={GeneratorStatusKind.REPORTED}
        groups={groups}
        getStatus={(g) => g.reportedStatus}
        isStart={(g) => g.reportedStart}
        isEnd={(g) => g.reportedEnd}
        hoveredMonth={hoveredMonth}
        onMonthHover={onMonthHover}
        onMonthLeave={onMonthLeave}
        // The reported row sits at the top of the band; extend the hover
        // overlay upward to reach the x-axis gap.
        highlightTop={-40}
        highlightBottom={0}
        gapBelow
      />
      <StatusRow
        label="Calculated"
        kind={GeneratorStatusKind.CALCULATED}
        groups={groups}
        getStatus={(g) => g.calculatedStatus}
        isStart={(g) => g.calculatedStart}
        isEnd={(g) => g.calculatedEnd}
        hoveredMonth={hoveredMonth}
        onMonthHover={onMonthHover}
        onMonthLeave={onMonthLeave}
        highlightTop={0}
        highlightBottom={-8}
        showDivergenceFlag
      />
    </Box>
  );
}

function StatusRow({
  label,
  kind,
  groups,
  getStatus,
  isStart,
  isEnd,
  hoveredMonth,
  onMonthHover,
  onMonthLeave,
  highlightTop,
  highlightBottom,
  gapBelow,
  showDivergenceFlag,
}: {
  label: string;
  kind: GeneratorStatusKind;
  groups: GroupedBandCell[];
  getStatus: (g: GroupedBandCell) => RcraInfoFederalWasteGeneratorStatus | null;
  isStart: (g: GroupedBandCell) => boolean;
  isEnd: (g: GroupedBandCell) => boolean;
  hoveredMonth: string | null;
  onMonthHover: (month: string) => void;
  onMonthLeave: () => void;
  highlightTop: number;
  highlightBottom: number;
  gapBelow?: boolean;
  /** When true, render a divergence indicator on cells where the WADM-derived
   * status disagrees with the shipment-derived status. Only meaningful on the
   * Calculated row — the comparison is shipment-vs-generation. */
  showDivergenceFlag?: boolean;
}) {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", alignItems: "center", mb: gapBelow ? 0.5 : 0 }}>
      <Box
        sx={{
          width: 80,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          pr: 1,
          minHeight: 32,
        }}
      >
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>{label}</Typography>
      </Box>
      <Box sx={{ display: "flex", flex: 1 }}>
        {groups.map((cell) => {
          const status = getStatus(cell);
          const colors = getFederalGeneratorStatusColorSettings(status, kind);
          const start = isStart(cell);
          const end = isEnd(cell);
          return (
            <Box
              key={`${label}-${cell.month}`}
              onMouseEnter={() => onMonthHover(cell.month)}
              onMouseLeave={onMonthLeave}
              sx={{ position: "relative", flex: 1, overflow: "hidden", cursor: "default" }}
            >
              {cell.month === hoveredMonth && (
                <Box
                  sx={{
                    position: "absolute",
                    top: highlightTop,
                    left: 0,
                    right: 0,
                    bottom: highlightBottom,
                    backgroundColor: theme.palette.grey[500],
                    opacity: 0.2,
                    zIndex: 1000,
                    pointerEvents: "none",
                  }}
                />
              )}
              <Box
                sx={{
                  minHeight: 32,
                  marginLeft: start ? 0.25 : 0,
                  marginRight: end ? 0.25 : 0,
                  backgroundColor: colors.backgroundColor,
                  display: "flex",
                  paddingLeft: 1,
                  paddingY: 1,
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                {start && (
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.8125rem",
                      color: colors.textColor,
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {federalWasteGeneratorStatusToLabel(status ?? null)}
                  </Typography>
                )}
                {showDivergenceFlag && cell.divergesFromShipment && (
                  <Tooltip
                    title={
                      <span>
                        Shipment data calculates{" "}
                        <strong>
                          {federalWasteGeneratorStatusToLabel(cell.shipmentCalculatedStatus)}
                        </strong>{" "}
                        for this month, but WADM generation calculates{" "}
                        <strong>{federalWasteGeneratorStatusToLabel(status ?? null)}</strong>. Worth
                        checking before reporting a status change.
                      </span>
                    }
                    placement="top"
                    arrow
                  >
                    <WarningAmberIcon
                      sx={{
                        fontSize: "1rem",
                        color: amber[800],
                        cursor: "help",
                        flexShrink: 0,
                        marginRight: 0.5,
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export function WasteGenerationGraph({
  data,
  totalFederalHazardousLbs,
  totalStateOnlyLbs,
  totalAcuteHazardousLbs,
  highestCalculatedGeneratorStatus,
  facilityState,
}: Props) {
  const thresholds = useMemo(() => getGenerationThresholds(facilityState), [facilityState]);
  const axisThresholds = useMemo<AxisThreshold[]>(
    () =>
      thresholds.map((t) => ({
        id: t.name,
        name: t.name,
        value: t.thresholdLbs,
        color: t.color,
        displayValue: `${formatNumber(t.thresholdLbs)} lbs`,
      })),
    [thresholds]
  );

  const dataset = useMemo(
    () =>
      data.map((row, index) => ({
        month: formatMonth(monthDateToString(row.month), index === 0),
        rawMonth: monthDateToString(row.month),
        federal: row.federalHazardousLbs,
        stateOnly: row.stateOnlyLbs,
      })),
    [data]
  );

  const monthlyStatus = useMemo(
    () => data.map((row) => row.calculatedFederalGeneratorStatus ?? null),
    [data]
  );

  const bandData = useMemo(
    () =>
      data.map((row) => ({
        month: monthDateToString(row.month),
        reportedStatus: row.reportedFederalGeneratorStatus ?? null,
        calculatedStatus: row.calculatedFederalGeneratorStatus ?? null,
        shipmentCalculatedStatus: row.shipmentCalculatedFederalGeneratorStatus ?? null,
        divergesFromShipment: row.divergesFromShipmentCalculatedStatus,
      })),
    [data]
  );

  const year = useMemo(
    () => (data[0] ? monthDateToString(data[0].month).slice(0, 4) : null),
    [data]
  );

  const [hoveredMonth, setHoveredMonth] = useState<string | null>(null);
  const handleMonthHover = useCallback((month: string) => setHoveredMonth(month), []);
  const handleMonthLeave = useCallback(() => setHoveredMonth(null), []);

  // Manual hover tracking on the chart so the whole band column highlights on
  // hover (matching the Waste Shipments tile), and a click anywhere in the
  // column opens the Detail View on that month. We compute `barIndex` from
  // `clientX - leftAxisOffset` rather than relying on bar-element click
  // targets so short / zero-height bars are still clickable.
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const monthIndexFromEvent = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): number | null => {
      const node = chartContainerRef.current;
      if (!node || dataset.length === 0) return null;
      const rect = node.getBoundingClientRect();
      // y-axis takes the leftmost 80 px (matches margin.left + yAxis.width below).
      const leftAxisOffset = 80;
      const chartWidth = rect.width - leftAxisOffset;
      if (chartWidth <= 0) return null;
      const x = event.clientX - rect.left - leftAxisOffset;
      if (x < 0) return null;
      const barWidth = chartWidth / dataset.length;
      const index = Math.floor(x / barWidth);
      return index >= 0 && index < dataset.length ? index : null;
    },
    [dataset]
  );
  const handleChartMouseMove = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const index = monthIndexFromEvent(event);
      if (index == null) {
        setHoveredMonth((prev) => (prev === null ? prev : null));
        return;
      }
      const month = dataset[index].rawMonth;
      setHoveredMonth((prev) => (prev === month ? prev : month));
    },
    [monthIndexFromEvent, dataset]
  );

  // Detailed Month View state. Default to the earliest month with generator-
  // status activity (federal / state-only / acute > 0) — that's what drives the
  // band on the chart, so opening the details panel there gives the picker a
  // meaningful starting point. Falls back to a month with any byClassification
  // data, or to the first month overall.
  const [monthDetailsCollapsed, setMonthDetailsCollapsed] = useState(true);
  const defaultSelectedMonth = useMemo(() => {
    const monthWithStatus = data.find(
      (row) => row.federalHazardousLbs > 0 || row.stateOnlyLbs > 0 || row.acuteHazardousLbs > 0
    );
    const monthWithAnyData = data.find((row) => (row.byClassification ?? []).length > 0);
    const pick = monthWithStatus ?? monthWithAnyData ?? data[0] ?? null;
    return pick ? monthDateToString(pick.month) : null;
  }, [data]);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(defaultSelectedMonth);
  // If the date range filter shifts and the previously-selected month falls
  // outside the new window, fall back to the default so the picker doesn't
  // point at a month that's no longer in `data`.
  const availableMonths = useMemo(() => data.map((row) => monthDateToString(row.month)), [data]);
  useEffect(() => {
    if (selectedMonth && !availableMonths.includes(selectedMonth) && defaultSelectedMonth) {
      setSelectedMonth(defaultSelectedMonth);
    }
  }, [selectedMonth, availableMonths, defaultSelectedMonth]);

  const handleChartClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const index = monthIndexFromEvent(event);
      if (index == null) return;
      setSelectedMonth(dataset[index].rawMonth);
      setMonthDetailsCollapsed(false);
    },
    [monthIndexFromEvent, dataset]
  );

  const hoveredMonthIndex = useMemo(() => {
    if (!hoveredMonth) return null;
    const i = dataset.findIndex((d) => d.rawMonth === hoveredMonth);
    return i >= 0 ? i : null;
  }, [dataset, hoveredMonth]);

  const highlightedAxis = useMemo(
    () => (hoveredMonthIndex !== null ? [{ axisId: "x-axis", dataIndex: hoveredMonthIndex }] : []),
    [hoveredMonthIndex]
  );

  // Round y-axis max up to a clean multiple of 500 so MUI's auto-tick labels
  // render cleanly (otherwise the topmost tick lands at the unrounded max and
  // shows an empty label). Larger step than the kg version since lb values
  // span a ~2.2× wider range for the same waste mass.
  const maxValue = useMemo(() => {
    const maxBar = data.reduce(
      (max, m) => Math.max(max, m.federalHazardousLbs + m.stateOnlyLbs),
      0
    );
    const maxThreshold = thresholds.reduce((max, t) => Math.max(max, t.thresholdLbs), 0);
    const base = Math.max(maxBar, maxThreshold, 100);
    const sqrt = Math.sqrt(base) * 1.15;
    const padded = sqrt * sqrt;
    const tickStep = 500;
    return Math.ceil(padded / tickStep) * tickStep;
  }, [data, thresholds]);

  return (
    <>
      <Stack
        direction={{ sm: "column", md: "row" }}
        spacing={{ sm: 0, md: 10 }}
        marginBottom={{ sm: 2, md: 0 }}
        flex={1}
      >
        <WasteValueDisplay label="Federal hazardous" value={totalFederalHazardousLbs} />
        <WasteValueDisplay label="State-only" value={totalStateOnlyLbs} />
        <WasteValueDisplay label="Acute hazardous" value={totalAcuteHazardousLbs} />
        <WasteValueDisplay
          label="Highest calculated generator status"
          value={federalWasteGeneratorStatusToLabel(highestCalculatedGeneratorStatus ?? null)}
        />
      </Stack>

      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" alignItems="center">
          <Typography variant="subtitle1">Waste generated by month in lbs</Typography>
          <BigTooltip
            title={
              <Typography variant="caption">
                Month-by-month waste generation from Amazon's Waste and Donation Management (WADM)
                feed, compared against federal generator-status thresholds. Unlike Waste Shipments
                (which can bundle several months of accumulated waste into one shipment), this
                reflects when waste was actually generated. Acute hazardous (
                <Box component="span" sx={{ color: ACUTE_COLOR, fontWeight: 600 }}>
                  red
                </Box>
                ) is shown as a separate stat and is currently 0 — WADM doesn't distinguish acute
                P-codes today.
              </Typography>
            }
          >
            <IconButton size="small">
              <InfoOutlined color="disabled" />
            </IconButton>
          </BigTooltip>
        </Stack>
      </Stack>

      <Box
        ref={chartContainerRef}
        onMouseMove={handleChartMouseMove}
        onMouseLeave={handleMonthLeave}
        onClick={handleChartClick}
        sx={{ height: 300, width: "100%", cursor: "pointer" }}
      >
        <ChartContainerPro
          dataset={dataset}
          series={[
            {
              type: "bar",
              dataKey: "federal",
              stack: "generation",
              color: FEDERAL_BAR_COLOR,
              label: "Federal hazardous",
              valueFormatter: (value) => `${formatNumber(value ?? 0)} lbs`,
            },
            {
              type: "bar",
              dataKey: "stateOnly",
              stack: "generation",
              color: STATE_ONLY_BAR_COLOR,
              label: "State-only",
              valueFormatter: (value) => `${formatNumber(value ?? 0)} lbs`,
            },
          ]}
          sx={{
            "& .MuiChartsAxisHighlight-root": {
              fill: (theme) => theme.palette.grey[500],
              fillOpacity: 0.2,
            },
          }}
          highlightedAxis={highlightedAxis}
          xAxis={[
            {
              id: "x-axis",
              scaleType: "band",
              dataKey: "month",
              categoryGapRatio: 0.3,
              height: 40,
            },
          ]}
          yAxis={[
            {
              id: "y-axis",
              scaleType: "sqrt",
              min: 0,
              max: maxValue,
              valueFormatter: (value) => formatNumber(value),
              width: 60,
            },
          ]}
          margin={{ left: 20, right: 20, top: 16, bottom: 0 }}
          height={300}
        >
          <BarPlot
            slotProps={{
              bar: { rx: 3, ry: 3, style: { strokeWidth: 1, stroke: "white" } },
            }}
          />
          <ChartsAxisHighlight x="band" />
          <ChartsXAxis />
          <ChartsYAxis />
          <ThresholdReferenceLines thresholds={axisThresholds} />
          <ThresholdYAxisIndicators thresholds={axisThresholds} />
          <ChartsTooltipContainer trigger="axis">
            <GenerationAxisTooltip year={year} monthlyStatus={monthlyStatus} />
          </ChartsTooltipContainer>
        </ChartContainerPro>
      </Box>

      {bandData.length > 0 && (
        <StatusBand
          monthly={bandData}
          hoveredMonth={hoveredMonth}
          onMonthHover={handleMonthHover}
          onMonthLeave={handleMonthLeave}
        />
      )}

      {data.length > 0 && (
        <Stack spacing={2} mt={1}>
          <Box>
            <Button
              onClick={() => setMonthDetailsCollapsed((prev) => !prev)}
              endIcon={monthDetailsCollapsed ? <KeyboardArrowDown /> : <KeyboardArrowUp />}
            >
              {monthDetailsCollapsed ? "View Month Details" : "Collapse Month Details"}
            </Button>
          </Box>
          <Collapse in={!monthDetailsCollapsed} unmountOnExit>
            <MonthDetailsView
              selectedMonth={selectedMonth}
              onMonthChange={setSelectedMonth}
              data={data}
            />
          </Collapse>
        </Stack>
      )}
    </>
  );
}

/**
 * The GraphQL `Date` scalar deserializes as either a `Date` object or a raw
 * `yyyy-mm-dd` string depending on the path; normalize to the string form the
 * downstream formatters expect.
 */
function monthDateToString(value: Date | string): string {
  if (typeof value === "string") return value.slice(0, 10);
  return value.toISOString().slice(0, 10);
}
